import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Clock, ShieldCheck } from "lucide-react";
import { SITE, IMAGES } from "../data/site";
import { PRODUCTS } from "../data/products";
import { useLang } from "../i18n";
import { MaskedLine, Reveal } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { SmartImage } from "../components/SmartImage";

export default function Quote() {
  const [params] = useSearchParams();
  const { t } = useLang();
  const slug = params.get("product");
  const product = PRODUCTS.find((p) => p.slug === slug);

  useEffect(() => {
    document.title = "Request a Quote | Sana Steel Industries, Bengaluru";
  }, []);

  const STEPS = [
    [ShieldCheck, t("step1.t"), t("step1.d")],
    [Phone, t("step2.t"), t("step2.d")],
    [Clock, t("step3.t"), t("step3.d")],
  ];

  return (
    <main data-testid="quote-page" className="pt-20">
      <section className="bg-navy py-24 md:py-28" data-testid="quote-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <MaskedLine delay={0.1}>
            <span className="section-eyebrow-dark inline-flex items-center gap-3">
              <span className="w-8 h-[2px] bg-silver inline-block" />
              {t("hero.cta1")}
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-white leading-[0.95] text-5xl md:text-7xl">
            <MaskedLine delay={0.25}>{t("quote.l1")}</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-silver">{t("quote.l2")}</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="quote-form-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="relative hidden lg:block mb-10">
                <div className="absolute -top-4 -left-4 w-full h-full border-2 border-steel" />
                <SmartImage src={product ? product.image : IMAGES.hero}
                            alt={product ? product.name : "Sana Steel scaffolding on site"}
                            className="relative w-full h-56 object-cover" />
              </div>
              <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-navy">
                {product ? `${t("quote.enquiring")} ${product.name}` : t("quote.how")}
              </h2>
              <ul className="mt-8 space-y-6">
                {STEPS.map(([Icon, title, desc], i) => (
                  <li key={title} className="flex items-start gap-4" data-testid={`quote-step-${i + 1}`}>
                    <span className="w-10 h-10 bg-surface border border-gray-200 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-navy" />
                    </span>
                    <div>
                      <p className="font-heading text-sm font-bold uppercase tracking-wide text-navy">{title}</p>
                      <p className="mt-1 text-sm text-steel leading-relaxed">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-10 border-t border-gray-200 pt-8">
                <p className="field-label !mb-2">{t("quote.talk")}</p>
                <a href={SITE.phone2Href} data-testid="quote-call-link"
                   className="font-heading text-2xl font-black tracking-tight text-navy hover:text-steel transition-colors duration-200">
                  {SITE.phone2}
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-8" delay={0.1}>
            <div className="bg-white border border-gray-200 p-8 md:p-12">
              <QuoteForm defaultProduct={product ? product.name : ""} />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
